import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useQuery from "@/hooks/useQuery";
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import { DropdownSort } from '@/shared-components/dropdown';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import Text from '@/shared-components/text/components/Text';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import ProfileArtworkDrafts from "../artworks-drafts";
import PopUpCreateOptions from "../drafts/components/PopUpCreateOptions.jsx";
import PopUpDrafts from "../drafts/components/PopUpDrafts";
import MetaDescripProfilePages from "../MetaDescripProfilePages";

import VirtualList from './components/VirtualList';

import './index.css';

export default function ProfileArtworks() {
  const { profileData, internal } = useContext(ProfileDataContext);
  const t = useTranslations('common.profile.pages.artworks');
  const navigate = useNavigate();
  const { state } = useStateHandler();
  const session = state?.user_session;
  const profileTitle = profileData?.user_profile_name ?? profileData?.user_name ?? profileData?.user_username ?? '';
  const [params, setParams] = useGetSearchParams({ validParams: ["sort", "user_type"] });
  const [query, setQuery] = useState(params || { sort: "Recent", user_type: profileData.user_type });
  const sortLabelByValue = useMemo(() => ({
    Recent: t('order_by.options.recent'),
    Relevant: t('order_by.options.relevant'),
    'High to low': t('order_by.options.high_to_low'),
    'Low to high': t('order_by.options.low_to_high'),
    'Limited editions': t('order_by.options.limited_editions'),
  }), [t]);
  const { loading, queryData, loadMoreData } = useQuery(
    `${profileData._id}_artworks`,
    `${config.apis.api.url}/artworks/${profileData._id}`,
    query,
    {
      invalidateWhen: internal
        ? ["BUY_ARTWORK", "EDIT_ARTWORK", "DELETE_ARTWORK", `${profileData._id}_REFRESH`]
        : [`${profileData._id}_REFRESH`],
    }
  );
  const navigateToArtwork = useNavigateToArtwork(
    `${profileData._id}_artworks`,
    queryData?.data,
    `/artworks/${profileData._id}${queryData?.queryString}`,
    { type: 'profile-navigation', label: profileTitle }
  );
  const publicArtworkCount = queryData?.data?.totalDocs ?? queryData?.data?.data?.length ?? 0;

  function selectSortOption(option) {
    const newQuery = { ...query, sort: option };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  const defaultPopUpValue = false;
  const [openOptions, setOpenOptions] = useState(defaultPopUpValue);
  const [openDraftPopup, setOpenDraftPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const [artistCanUpload, setArtistCanUpload] = useState(false);
  const isArtistProfile = internal && (profileData.user_type === "artist" || profileData.user_type === "gallery_artist");
  const galleryArtistField = profileData?.gallery_artist;
  const artistPermissionId = typeof galleryArtistField === "string" ? galleryArtistField : galleryArtistField?._id;
  const galleryPermissionId = (typeof galleryArtistField?.gallery === "string"
    ? galleryArtistField.gallery
    : galleryArtistField?.gallery?._id) || profileData?.gallery_id;
  const draftsCacheKey = profileData?._id ? `${profileData._id}_artwork_drafts` : 'artwork_drafts';
  const draftInvalidateKeys = [
    "ARTWORK_DRAFT_SAVED",
    "NEW_ARTWORK_REQUEST",
    profileData?._id ? `${profileData._id}_REFRESH` : null,
  ].filter(Boolean);

  const { fetchData: artistDraftsData } = useCache(
    draftsCacheKey,
    `${config.apis.api.url}/drafts`,
    {
      injectToken: true,
      invalidateWhen: draftInvalidateKeys,
      disabled: !isArtistProfile,
    }
  );

  useEffect(() => {
    if (!isArtistProfile || !artistPermissionId || !galleryPermissionId) {
      setArtistCanUpload(false);
      return;
    }

    let isActive = true;

    (async () => {
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/permissions/check?artist=${artistPermissionId}&gallery=${galleryPermissionId}`,
          { injectToken: true }
        );
        if (!response.ok) {
          if (isActive) setArtistCanUpload(false);
          return;
        }
        const canUpload = Boolean(data?.permissions?.permissions?.addArtworks);
        if (isActive) setArtistCanUpload(canUpload);
      } catch (error) {
        if (isActive) setArtistCanUpload(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [artistPermissionId, galleryPermissionId, isArtistProfile]);

  const canUploadArtwork = internal && (
    (profileData.user_type === "gallery"
      && canAccessGalleryPermission(session, "artworks.add", profileData?._id))
    || (isArtistProfile && artistCanUpload)
  );
  const isGalleryProfile = internal && profileData.user_type === "gallery";
  const showAddArtworkButton = isGalleryProfile || (isArtistProfile && artistCanUpload);
  const artistHasDrafts = (artistDraftsData?.data?.length ?? 0) > 0;

  const handleAddArtworkClick = () => {
    if (!canUploadArtwork) {
      setShowActionRejectedPopup(true);
      return;
    }

    if (!isGalleryProfile) {
      if (isArtistProfile && artistHasDrafts) {
        setOpenOptions(!openOptions);
        return;
      }
      navigate("/profile/add-artwork");
      return;
    }

    if (profileData?.draft_count === 0) {
      navigate("/profile/add-artwork");
      return;
    }

    setOpenOptions(!openOptions);
  };

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="artworks"
        internal={internal}
        itemCount={publicArtworkCount}
      />
      {showAddArtworkButton && (
        <>
          <ProfileAddButton
            label={t('buttons.add_artwork')}
            onClick={handleAddArtworkClick}
          />
          {(isGalleryProfile || isArtistProfile) && (
            <>
              <PopUpCreateOptions
                open={openOptions}
                close={() => setOpenOptions(defaultPopUpValue)}
                openDraftPopup={setOpenDraftPopup}
                textOption={t('labels.artwork')}
                to="/profile/add-artwork"
                requireDraftsForOpen={isArtistProfile}
                enableQueue={isGalleryProfile}
              />

              <PopUpDrafts
                open={openDraftPopup}
                close={() => setOpenDraftPopup(false)}
                DraftsList={ProfileArtworkDrafts}
              />
            </>
          )}
          <ActionRejectedPopup
            open={showActionRejectedPopup}
            close={() => setShowActionRejectedPopup(false)}
            description={t('messages.permission_denied')}
          />
        </>
      )}
      {(!queryData || (queryData?.data.data.length === 0 && loading)) ? (
        <div className="profile-artworks-loader-container">
          <ProfileContentLoader />
        </div>
      ) : queryData?.data.data.length === 0 && !loading ? (
        <Text className="user-profile__artworks-empty-view font-size-medium" paragraph>
          {internal ? (
            profileData.user_type === "gallery" ? (
              t('empty.internal_gallery')
            ) : (
              t('empty.internal_artist')
            )
          ) : (
            t('empty.external')
          )}
        </Text>
      ) : (
        <>
          <DropdownSort
            defaultOption={query.sort}
            options={config.search.profile.artwork_sort_options}
            onChangeOption={selectSortOption}
            getOptionLabel={(option) => sortLabelByValue[option] || option}
          />

          {loading ? (
            <div className="profile-artworks-loader-container">
              <ProfileContentLoader />
            </div>
          ) : (
            <VirtualList
              key={queryData.queryString}
              items={queryData.data}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
            />
          )}
        </>
      )}
    </>
  );
}
